import { WifiOff, Crown, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useOfflineMode } from '@/hooks/useOfflineMode';

interface OfflineUpgradeRequiredProps {
  isLoggedIn: boolean;
}

export const OfflineUpgradeRequired = ({ isLoggedIn }: OfflineUpgradeRequiredProps) => {
  const { t } = useTranslation();
  const { isOffline, showUpgradeOverlay, isInitialized } = useOfflineMode();

  // Don't show anything until initialization is complete
  // This prevents the brief flash of overlay while session is being validated
  if (!isInitialized) {
    return null;
  }

  // Use the hook's computed state instead of managing our own
  if (!showUpgradeOverlay) return null;

  const handleUpgrade = () => {
    // Store intent, will redirect when online
    sessionStorage.setItem('pendingUpgrade', 'true');
  };

  const handleLogin = () => {
    sessionStorage.setItem('pendingLogin', 'true');
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-2 border-destructive/20 shadow-2xl">
        <CardContent className="pt-8 pb-8 text-center space-y-6">
          <div className="mx-auto w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center">
            <WifiOff className="h-10 w-10 text-destructive" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-foreground">
              {t('offline.upgradeRequired.title', 'Offline Mode Requires a Subscription')}
            </h2>
            <p className="text-muted-foreground">
              {t('offline.upgradeRequired.description', 'Access your saved recipes offline with Pro or Chef Master')}
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">
              {t('offline.upgradeRequired.connectToInternet', 'Connect to the internet to continue')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('offline.upgradeRequired.orUpgrade', 'Or upgrade your account for offline access')}
            </p>
          </div>

          <div className="space-y-3">
            {!isLoggedIn ? (
              <Button 
                onClick={handleLogin}
                className="w-full"
                size="lg"
              >
                <LogIn className="mr-2 h-5 w-5" />
                {t('offline.upgradeRequired.loginToPaid', 'Login to Paid Account')}
              </Button>
            ) : null}
            
            <Button 
              onClick={handleUpgrade}
              variant={isLoggedIn ? 'default' : 'outline'}
              className="w-full"
              size="lg"
            >
              <Crown className="mr-2 h-5 w-5" />
              {t('offline.upgradeRequired.upgrade', 'Upgrade to Pro')}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            {t('offline.upgradeRequired.waitingForConnection', 'Waiting for internet connection...')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
